import { DependencyType } from '@koppla-tech/scheduling-engine';

import {
  CalendarEntity,
  MilestoneEntity,
  OrderDependencyEntity,
  OrderEntity,
} from '@/common/types/entities';
import { MilestoneType } from '@/features/milestones/types';
import { SectionsMap } from '@/features/projectStructure/utils/sectionTreeUtils';
import { getStatusReportOfStatus } from '@/helpers/orders/status';
import { Entity } from '@/interfaces/repositories/base';
import { NodeName } from '@/repositories/utils/cache';
import type {
  BulkOrder,
  BulkOrderDependency,
  CalendarDto,
  MilestoneDto,
  ProjectPauseDto,
  WbsSectionDto,
} from '@/services/store/bulkApiClient';

/**
 * Returns generic onError function for stores to update the state based on given new fragment
 * @param state
 * @param id
 * @returns
 */
export function getOnError<T extends Entity>(
  state: Map<string, T>,
  fragments: T[],
  options: {
    delete: boolean;
  } = { delete: false },
): () => void {
  return () => {
    fragments.forEach((fragment) => {
      if (options.delete) {
        state.delete(fragment.id);
      } else {
        state.set(fragment.id, fragment);
      }
    });
  };
}

export function convertOrders(orders: BulkOrder[]): Map<string, OrderEntity> {
  return new Map(
    orders.map((order) => {
      return [
        order.id,
        {
          __typename: NodeName.ORDER,
          ...order,
          startAt: new SchedulingDate(order.startAt),
          finishAt: new SchedulingDate(order.finishAt),
          finishedAt:
            order.finishedAt === null ? order.finishedAt : new SchedulingDate(order.finishedAt),
          status: getStatusReportOfStatus(order.status),
          progress: order.progress ?? 0,
          wbsSection: {
            ...order.wbsSection!,
            __typename: NodeName.SECTION,
          },
          calendar: { ...order.calendar, __typename: NodeName.CALENDAR },
          tenantTradeVariation: order.tenantTradeVariation!,
          contributorGroup: order.contributorGroup ? { id: order.contributorGroup.id } : null,
          tradeSequenceActivity: order.tradeSequenceActivity
            ? {
                ...order.tradeSequenceActivity,
                __typename: NodeName.TRADE_SEQUENCE_ACTIVITY,
              }
            : null,
        },
      ];
    }),
  );
}

export function convertWbsSections(sections: WbsSectionDto[]): SectionsMap {
  return new Map(
    sections.map((s) => {
      return [
        s.id,
        {
          ...s,
          parentId: s.parent?.id ?? null,
        },
      ];
    }),
  );
}

export function convertMilestones(milestones: MilestoneDto[]): Map<string, MilestoneEntity> {
  const milestoneEntities: MilestoneEntity[] = milestones.map((milestone) => {
    return {
      ...milestone,
      date: new SchedulingDate(milestone.date),
      acceptanceCriteria: [],
      isFixed: milestone.isFixed,
      type: milestone.isFixed ? MilestoneType.FIXED : MilestoneType.FLEXIBLE,
      completedAt: milestone.completedAt ? new Date(milestone.completedAt) : null,
    };
  });

  return new Map(milestoneEntities.map((m) => [m.id, m]));
}

export function convertPauses(pauses: ProjectPauseDto[]): Map<string, any> {
  return new Map(
    pauses.map((pause) => {
      return [
        pause.id,
        {
          ...pause,
          start: new SchedulingDate(pause.start),
          end: new SchedulingDate(pause.end),
        },
      ];
    }),
  );
}

export function convertDependencies(
  dependencies: BulkOrderDependency[],
): Map<string, OrderDependencyEntity> {
  return new Map(
    dependencies.map((dependency) => {
      const depEntity: OrderDependencyEntity = {
        ...dependency,
        type: dependency.type as DependencyType,
        from: dependency.from.milestoneId
          ? { __typename: 'ProjectMilestoneNode', id: dependency.from.milestoneId }
          : { __typename: 'OrderNode', id: dependency.from.orderId! },
        to: dependency.to.milestoneId
          ? { __typename: 'ProjectMilestoneNode', id: dependency.to.milestoneId }
          : { __typename: 'OrderNode', id: dependency.to.orderId! },
      };

      return [dependency.id, depEntity];
    }),
  );
}

export function convertCalendars(calendars: CalendarDto[]): Map<string, CalendarEntity> {
  return new Map(
    calendars.map((calendar) => {
      const calendarEntity: CalendarEntity = {
        ...calendar,
        exceptions: calendar.exceptions.map((exception) => {
          return {
            ...exception,
            startAt: new SchedulingDate(exception.startAt),
            finishAt: new SchedulingDate(exception.finishAt),
          };
        }),
      };

      return [calendar.id, calendarEntity];
    }),
  );
}
